import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { IncidentTimeDataPoint } from './aggregateIncidents';
import {
  formatTimeWithUnit,
  logPosition,
  computeNiceDomain,
  generateLogTicks,
  seededRandom,
} from './logScaleUtils';

const SEVERITY_COLORS: Record<string, string> = {
  'SEV 1': '#F56565',
  'SEV 2': '#ED8936',
  'SEV 3': '#ECC94B',
  'SEV 4': '#9F7AEA',
  'TBD': '#A0AEC0',
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  plotArea: {
    position: 'relative',
    height: 44,
    marginBottom: theme.spacing(1),
  },
  dotContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 16,
    display: 'flex',
    alignItems: 'center',
  },
  dot: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: '1.5px solid #fff',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'scale(1.3)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
      zIndex: 10,
    },
  },
  referenceLine: {
    position: 'absolute',
    top: 0,
    bottom: 16,
    width: 2,
    transform: 'translateX(-50%)',
  },
  medianLine: {
    backgroundColor: '#3182CE',
  },
  meanLine: {
    backgroundColor: 'transparent',
    borderLeft: '2px dashed #A0AEC0',
    width: 0,
  },
  axis: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 16,
    borderTop: '1px solid #E2E8F0',
  },
  tick: {
    position: 'absolute',
    transform: 'translateX(-50%)',
  },
  tickMark: {
    width: 1,
    height: 3,
    backgroundColor: '#CBD5E0',
  },
  tickLabel: {
    fontSize: 8,
    color: '#A0AEC0',
    marginTop: 2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.5),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  statIndicator: {
    width: 8,
    height: 2,
  },
  medianIndicator: {
    backgroundColor: '#3182CE',
  },
  meanIndicator: {
    background: 'repeating-linear-gradient(90deg, #A0AEC0 0px, #A0AEC0 2px, transparent 2px, transparent 4px)',
  },
  statLabel: {
    fontSize: 9,
    color: '#718096',
  },
  statValue: {
    fontSize: 9,
    fontWeight: 600,
    color: '#4A5568',
  },
  notRecordedText: {
    fontSize: 9,
    color: '#A0AEC0',
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
  },
  emptyState: {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#A0AEC0',
    fontSize: 11,
    fontStyle: 'italic',
    backgroundColor: '#F7FAFC',
    borderRadius: 4,
  },
  tooltipContent: {
    maxWidth: 280,
  },
  tooltipTitle: {
    fontWeight: 600,
    marginBottom: 4,
    lineHeight: 1.3,
  },
  tooltipDetail: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 2,
  },
}));

interface TimeDistributionDotPlotProps {
  dataPoints: IncidentTimeDataPoint[];
  median: number | null;
  mean: number | null;
  notRecordedCount: number;
  metricLabel: string; // "TTD" or "TTR"
}

export const TimeDistributionDotPlot: React.FC<TimeDistributionDotPlotProps> = ({
  dataPoints,
  median,
  mean,
  notRecordedCount,
  metricLabel,
}) => {
  const classes = useStyles();
  const [hoveredDot, setHoveredDot] = useState<number | null>(null);

  const { domain, ticks, positionedDots } = useMemo(() => {
    if (dataPoints.length === 0) {
      return { domain: { min: 1, max: 60 }, ticks: [], positionedDots: [] };
    }

    const values = dataPoints.map((d) => d.value);
    const domain = computeNiceDomain(values);
    const ticks = generateLogTicks(domain.min, domain.max);

    // Position dots with jitter
    const positionedDots = dataPoints.map((point, index) => {
      const xPercent = logPosition(point.value, domain.min, domain.max) * 100;
      // Use seeded random for consistent jitter based on incident name
      const jitterSeed = point.incidentName + index;
      const yJitter = (seededRandom(jitterSeed) - 0.5) * 12; // +/- 6px
      return { ...point, xPercent, yJitter, index };
    });

    return { domain, ticks, positionedDots };
  }, [dataPoints]);

  const handleDotClick = (spreadsheetRowLink: string) => {
    if (spreadsheetRowLink) {
      window.open(spreadsheetRowLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Empty state: no recorded incidents
  if (dataPoints.length === 0) {
    return (
      <Box className={classes.container}>
        <Box className={classes.emptyState}>
          No recorded {metricLabel} data
        </Box>
        {notRecordedCount > 0 && (
          <Typography className={classes.notRecordedText}>
            {notRecordedCount} incident{notRecordedCount !== 1 ? 's' : ''} not recorded
          </Typography>
        )}
      </Box>
    );
  }

  // Single point: still render, but don't draw reference lines
  const showReferenceLines = dataPoints.length >= 2;

  return (
    <Box className={classes.container}>
      <Box className={classes.plotArea}>
        <Box className={classes.dotContainer}>
          {/* Reference lines */}
          {showReferenceLines && median !== null && (
            <Box
              className={`${classes.referenceLine} ${classes.medianLine}`}
              style={{ left: `${logPosition(median, domain.min, domain.max) * 100}%` }}
            />
          )}
          {showReferenceLines && mean !== null && (
            <Box
              className={`${classes.referenceLine} ${classes.meanLine}`}
              style={{ left: `${logPosition(mean, domain.min, domain.max) * 100}%` }}
            />
          )}

          {/* Dots */}
          {positionedDots.map((dot) => {
            const severityColor = SEVERITY_COLORS[dot.severity || 'TBD'] || SEVERITY_COLORS['TBD'];
            const isHovered = hoveredDot === dot.index;

            const tooltipContent = (
              <Box className={classes.tooltipContent}>
                <Typography className={classes.tooltipTitle}>
                  {dot.incidentName}
                </Typography>
                <Typography className={classes.tooltipDetail}>
                  <strong>{metricLabel}:</strong> {formatTimeWithUnit(dot.value)}
                </Typography>
                {dot.severity && (
                  <Typography className={classes.tooltipDetail}>
                    <strong>Severity:</strong> {dot.severity}
                  </Typography>
                )}
                {dot.detectionMethod && (
                  <Typography className={classes.tooltipDetail}>
                    <strong>Detection:</strong> {dot.detectionMethod}
                  </Typography>
                )}
                {dot.spreadsheetRowLink && (
                  <Typography className={classes.tooltipDetail} style={{ marginTop: 4, fontStyle: 'italic' }}>
                    Click to view in source
                  </Typography>
                )}
              </Box>
            );

            return (
              <Tooltip
                key={dot.index}
                title={tooltipContent}
                placement="top"
                arrow
              >
                <Box
                  className={classes.dot}
                  onClick={() => handleDotClick(dot.spreadsheetRowLink)}
                  onMouseEnter={() => setHoveredDot(dot.index)}
                  onMouseLeave={() => setHoveredDot(null)}
                  style={{
                    left: `${dot.xPercent}%`,
                    top: `calc(50% + ${dot.yJitter}px)`,
                    transform: `translate(-50%, -50%)${isHovered ? ' scale(1.3)' : ''}`,
                    backgroundColor: severityColor,
                    zIndex: isHovered ? 10 : 1,
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        {/* Axis */}
        <Box className={classes.axis}>
          {ticks.map((tick) => {
            const position = logPosition(tick, domain.min, domain.max) * 100;
            return (
              <Box
                key={tick}
                className={classes.tick}
                style={{ left: `${position}%` }}
              >
                <Box className={classes.tickMark} />
                <Typography className={classes.tickLabel}>
                  {formatTimeWithUnit(tick)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Stats row below plot */}
      <Box className={classes.statsRow}>
        <Box className={classes.statItem}>
          {showReferenceLines && median !== null && (
            <>
              <Box className={`${classes.statIndicator} ${classes.medianIndicator}`} />
              <Typography className={classes.statLabel}>median:</Typography>
              <Typography className={classes.statValue}>{formatTimeWithUnit(median)}</Typography>
            </>
          )}
        </Box>
        <Box className={classes.statItem}>
          {showReferenceLines && mean !== null && (
            <>
              <Box className={`${classes.statIndicator} ${classes.meanIndicator}`} />
              <Typography className={classes.statLabel}>mean:</Typography>
              <Typography className={classes.statValue}>{formatTimeWithUnit(mean)}</Typography>
            </>
          )}
        </Box>
      </Box>

      {notRecordedCount > 0 && (
        <Typography className={classes.notRecordedText}>
          {notRecordedCount} not recorded
        </Typography>
      )}
    </Box>
  );
};

export default TimeDistributionDotPlot;
